import { useEffect, useRef, useState } from 'react';
import { X, ShieldCheck, Loader2, CheckCircle2, XCircle, ImagePlus, RefreshCw } from 'lucide-react';
import { sendNomineeOtp, verifyNomineeOtp, uploadNomineePhoto } from '../api/voting';

export default function PhotoUpdateModal({ open, onClose, nominee }) {
  const [step, setStep]         = useState('idle');   // idle | sending | otp_input | verifying | upload | uploading | done
  const [otp, setOtp]           = useState('');
  const [otpErr, setOtpErr]     = useState('');
  const [flashErr, setFlashErr] = useState('');
  const [image, setImage]       = useState(null);
  const [preview, setPreview]   = useState(null);
  const fileRef                 = useRef(null);
  const verifiedOtp             = useRef('');

  useEffect(() => {
    if (open) {
      setStep('idle');
      setOtp(''); setOtpErr(''); setFlashErr('');
      setImage(null); setPreview(null);
      verifiedOtp.current = '';
    }
    return () => {
      document.body.classList.remove('modal-open-scroll-lock');
    };
  }, [open]);

  useEffect(() => {
    if (open) document.body.classList.add('modal-open-scroll-lock');
    else       document.body.classList.remove('modal-open-scroll-lock');
  }, [open]);

  if (!open) return null;

  const handleSendOtp = async () => {
    setFlashErr('');
    setStep('sending');
    try {
      const res = await sendNomineeOtp(nominee.votingCode);
      if (res?.status === 'success') {
        setStep('otp_input');
      } else {
        setFlashErr(res?.message || 'Could not send OTP.');
        setStep('idle');
      }
    } catch (e) {
      setFlashErr(e?.response?.data?.message || e?.message || 'Network error.');
      setStep('idle');
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp.trim()) { setOtpErr('Please enter the OTP.'); return; }
    setOtpErr(''); setFlashErr('');
    setStep('verifying');
    try {
      const res = await verifyNomineeOtp(nominee.votingCode, otp.trim());
      if (res?.status === 'success') {
        verifiedOtp.current = otp.trim();
        setStep('upload');
      } else {
        setFlashErr(res?.message || 'Invalid OTP.');
        setStep('otp_input');
      }
    } catch (e) {
      setFlashErr(e?.response?.data?.message || e?.message || 'Network error.');
      setStep('otp_input');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImage(file);
    setPreview(URL.createObjectURL(file));
    setFlashErr('');
  };

  const handleUpload = async () => {
    if (!image) { setFlashErr('Please select a photo.'); return; }
    setFlashErr('');
    setStep('uploading');
    try {
      const res = await uploadNomineePhoto(nominee.votingCode, verifiedOtp.current, image);
      if (res?.status === 'success') {
        setStep('done');
      } else {
        setFlashErr(res?.message || 'Upload failed.');
        setStep('upload');
      }
    } catch (e) {
      setFlashErr(e?.response?.data?.message || e?.message || 'Network error.');
      setStep('upload');
    }
  };

  const isBusy = step === 'sending' || step === 'verifying' || step === 'uploading';

  return (
    <div
      className="payment-modal-backdrop"
      onClick={(e) => !isBusy && e.target === e.currentTarget && onClose()}
      role="dialog"
      aria-modal="true"
    >
      <div className="payment-modal card border-0 shadow-lg">

        {/* Header */}
        <div className="ink-surface p-4 position-relative">
          {!isBusy && step !== 'done' && (
            <button type="button" onClick={onClose} className="btn btn-sm position-absolute top-0 end-0 m-2 text-white opacity-75" aria-label="Close">
              <X size={18} />
            </button>
          )}
          <div className="d-flex align-items-center gap-3">
            <div className="rounded-circle gold-chip d-inline-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 42, height: 42 }}>
              <ShieldCheck size={20} />
            </div>
            <div style={{ minWidth: 0 }}>
              <h2 className="font-display h5 mb-0 text-white">Update Your Photo</h2>
              <p className="mb-0 small text-white-50 text-truncate">
                {nominee?.name} &bull; {nominee?.categoryName}
              </p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-4">

          {/* Flash error */}
          {flashErr && (
            <div className="alert alert-danger d-flex align-items-start gap-2 py-2 px-3 mb-3 small">
              <XCircle size={15} className="flex-shrink-0 mt-1" />
              <span>{flashErr}</span>
            </div>
          )}

          {/* Step: idle */}
          {step === 'idle' && (
            <>
              <p className="text-muted small lh-base mb-4">
                To update your photo, we need to verify your identity. Click the button below
                to receive a One-Time Password (OTP) on your registered contact.
              </p>
              <div className="d-flex gap-2">
                <button className="btn btn-outline-secondary flex-grow-1" onClick={onClose}>Cancel</button>
                <button className="btn gold-chip flex-grow-1 fw-semibold" onClick={handleSendOtp}>Send OTP</button>
              </div>
            </>
          )}

          {/* Step: sending */}
          {step === 'sending' && (
            <div className="text-center py-3">
              <Loader2 size={32} className="text-warning mb-2 spin" />
              <p className="text-muted small mb-0">Sending OTP to your registered contact…</p>
            </div>
          )}

          {/* Step: otp_input */}
          {step === 'otp_input' && (
            <>
              <p className="text-muted small mb-3">Enter the OTP sent to your registered contact.</p>
              <input
                type="text"
                value={otp}
                onChange={(e) => { setOtp(e.target.value.toUpperCase()); setOtpErr(''); }}
                placeholder="Enter OTP"
                className={`form-control form-control-lg text-center font-mono fw-bold mb-1 ${otpErr ? 'is-invalid' : ''}`}
                maxLength={8}
                autoFocus
              />
              {otpErr && <div className="invalid-feedback d-block mb-2">{otpErr}</div>}
              <button
                type="button"
                className="btn btn-link btn-sm text-muted p-0 mb-3 d-flex align-items-center gap-1"
                onClick={() => { setStep('idle'); setOtp(''); setOtpErr(''); setFlashErr(''); }}
              >
                <RefreshCw size={12} /> Resend OTP
              </button>
              <div className="d-flex gap-2">
                <button className="btn btn-outline-secondary flex-grow-1" onClick={onClose}>Cancel</button>
                <button className="btn gold-chip flex-grow-1 fw-semibold" onClick={handleVerifyOtp}>Verify OTP</button>
              </div>
            </>
          )}

          {/* Step: verifying */}
          {step === 'verifying' && (
            <div className="text-center py-3">
              <Loader2 size={32} className="text-warning mb-2 spin" />
              <p className="text-muted small mb-0">Verifying OTP…</p>
            </div>
          )}

          {/* Step: upload */}
          {step === 'upload' && (
            <>
              <div className="nominee-notice mb-3">
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 mt-1">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                <span>Select a good quality image/photo, crop it into a square for best display. JPG or PNG only. Max size: 2MB.</span>
              </div>
              <div
                className="nominee-photo-dropzone mb-3"
                onClick={() => fileRef.current?.click()}
              >
                {preview ? (
                  <img src={preview} alt="Preview" className="nominee-photo-preview" />
                ) : (
                  <div className="d-flex flex-column align-items-center gap-2 text-muted">
                    <ImagePlus size={28} />
                    <span className="small">Click to select photo</span>
                  </div>
                )}
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png"
                className="d-none"
                onChange={handleFileChange}
              />
              <div className="d-flex gap-2">
                <button className="btn btn-outline-secondary flex-grow-1" onClick={onClose}>Cancel</button>
                <button className="btn gold-chip flex-grow-1 fw-semibold" disabled={!image} onClick={handleUpload}>Upload Photo</button>
              </div>
            </>
          )}

          {/* Step: uploading */}
          {step === 'uploading' && (
            <div className="text-center py-3">
              <Loader2 size={32} className="text-warning mb-2 spin" />
              <p className="text-muted small mb-0">Uploading photo…</p>
            </div>
          )}

          {/* Step: done */}
          {step === 'done' && (
            <div className="text-center py-2">
              <div className="mx-auto rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                   style={{ width: 56, height: 56, background: 'rgba(25,135,84,0.15)' }}>
                <CheckCircle2 size={32} className="text-success" />
              </div>
              <h3 className="font-display fs-5 mb-1">Photo updated!</h3>
              <p className="text-muted small mb-3">Your photo has been submitted successfully.</p>
              <button className="btn gold-chip w-100 fw-semibold" onClick={() => { onClose(); window.location.reload(); }}>Done</button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
